import React, { useState, useEffect, useCallback, useContext, createContext, ReactNode } from 'react';
import { useSellers, Seller } from './useSellers';
import { useLots } from './useLots';
import { useComisiones } from './useComisiones';
import { useMovimientos } from './useMovimientos';
import { useUsuarios } from './useUsuarios';
import { useDevoluciones } from './useDevoluciones';
import { migrateMovimientosAutomatic } from '../migrateMovimientosAutomatic';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_CONFIG = {
  nombre: '',
  ubicacion: '',
  fecha: new Date().toISOString().split('T')[0],
  moneda: 'GTQ',
  lotes: 250,
  precioBase: 15000,
  filas: 10,
  columnas: 25,
  // Información adicional de la empresa para recibos
  razonSocial: '',
  nit: '',
  direccionCompleta: '',
  telefono1: '',
  telefono2: '',
  email: '',
  logoUrl: '', // URL de Firebase Storage o base64
  // Información bancaria
  numeroCuenta: '',
  tipoCuenta: '',
  nombreBanco: '',
  direccionBanco: '',
  telefonoBanco: '',
  // Configuración de Vendedores
  porcentajeComision: 5,
  tipoComision: 'porcentaje', // 'porcentaje' | 'fijo'
  montoComisionFijo: 0,
};

// 1. Crear el contexto
const AppDataContext = createContext<any | undefined>(undefined);

// 2. Provider
export function AppDataProvider({ children }: { children: ReactNode }) {
  // Usar los hooks de Firestore
  const {
    sellers,
    loading: sellersLoading,
    error: sellersError,
    addSeller,
    updateSeller,
    deleteSeller
  } = useSellers();

  const {
    lots,
    loading: lotsLoading,
    error: lotsError,
    addLot,
    updateLot,
    deleteLot,
    addPayment,
    returnLot,
    fetchLots
  } = useLots();

  const {
    comisiones,
    loading: comisionesLoading,
    error: comisionesError,
    addComision,
    updateComision,
    deleteComision,
    markComisionAsPaid,
    fetchComisiones
  } = useComisiones();

  const {
    movimientos,
    loading: movimientosLoading,
    error: movimientosError,
    addMovimiento,
    updateMovimiento,
    deleteMovimiento,
    fetchMovimientos
  } = useMovimientos();

  const {
    usuarios,
    loading: usuariosLoading,
    error: usuariosError,
    addUsuario,
    updateUsuario,
    deleteUsuario,
    toggleActivo: toggleUsuarioActivo,
    generarNuevoPin,
    buscarPorCredenciales,
    registrarIntentoFallido,
    resetearIntentosFallidos,
    fetchUsuarios
  } = useUsuarios();

  const {
    devoluciones,
    loading: devolucionesLoading,
    error: devolucionesError,
    fetchDevoluciones,
    addDevolucion,
    aprobarDevolucion,
    rechazarDevolucion,
  } = useDevoluciones();

  // Estado local para configuración - Firebase como fuente única de verdad
  const [config, setConfig] = useState<any>(DEFAULT_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);

  // Suscripción en tiempo real a la configuración desde Firebase
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupConfigListener = async () => {
      try {
        const { db } = await import('../firebaseConfig');
        const { doc, onSnapshot, setDoc } = await import('firebase/firestore');
        const configRef = doc(db, 'config', 'proyecto');

        unsubscribe = onSnapshot(configRef, async (docSnap) => {
          if (docSnap.exists()) {
            const configFirebase = docSnap.data();
            console.log('✅ Configuración sincronizada desde Firebase:', configFirebase);
            setConfig(configFirebase);
            // Actualizar caché local
            localStorage.setItem('bimobiConfig', JSON.stringify(configFirebase));
          } else {
            // Si no existe en Firebase, crear con valores por defecto
            console.log('⚙️ Creando configuración inicial en Firebase');
            await setDoc(configRef, DEFAULT_CONFIG);
            setConfig(DEFAULT_CONFIG);
            localStorage.setItem('bimobiConfig', JSON.stringify(DEFAULT_CONFIG));
          }
          setConfigLoading(false);
        }, (error) => {
          console.error('❌ Error en suscripción a configuración:', error);
          // Fallback a caché local
          const saved = localStorage.getItem('bimobiConfig');
          if (saved) {
            setConfig(JSON.parse(saved));
          }
          setConfigLoading(false);
        });
      } catch (error) {
        console.error('❌ Error al configurar listener de configuración:', error);
        // Fallback a caché local
        const saved = localStorage.getItem('bimobiConfig');
        if (saved) {
          setConfig(JSON.parse(saved));
        }
        setConfigLoading(false);
      }
    };

    setupConfigListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const [lotGroups, setLotGroups] = useState<any[]>(() => {
    try {
      const data = localStorage.getItem('bimobiLotGroups');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });

  const [lotHistory, setLotHistory] = useState<any[]>(() => {
    try {
      const data = localStorage.getItem('bimobiLotHistory');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });

  const [markedPayments, setMarkedPayments] = useState<any[]>(() => {
    try {
      const data = localStorage.getItem('bimobiPagosMarcados');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });

  // Persistir datos locales en localStorage
  useEffect(() => {
    localStorage.setItem('bimobiLotGroups', JSON.stringify(lotGroups));
  }, [lotGroups]);

  useEffect(() => {
    localStorage.setItem('bimobiLotHistory', JSON.stringify(lotHistory));
  }, [lotHistory]);

  useEffect(() => {
    localStorage.setItem('bimobiPagosMarcados', JSON.stringify(markedPayments));
  }, [markedPayments]);

  // Funciones de conveniencia que mantienen la API original
  const setLotsAndPersist = useCallback((newLots: any[]) => {
    // Esta función ya no es necesaria ya que useLots maneja la persistencia automáticamente
    console.warn('setLotsAndPersist is deprecated. Use addLot, updateLot, deleteLot from useLots instead.');
  }, []);

  const setExpensesAndPersist = useCallback((newExpenses: any[]) => {
    // Esta función ya no es necesaria ya que useMovimientos maneja la persistencia automáticamente
    console.warn('setExpensesAndPersist is deprecated. Use addMovimiento, updateMovimiento, deleteMovimiento from useMovimientos instead.');
  }, []);

  const saveAll = useCallback(() => {
    // Esta función ya no es necesaria ya que todos los hooks manejan la persistencia automáticamente
    console.warn('saveAll is deprecated. All data is automatically persisted to Firestore.');
  }, []);

  // Crear vendedor y usuario vendedor automáticamente
  // Cambiar el tipo de addSellerWithUser para aceptar Seller completo
  const addSellerWithUser = async (seller: Omit<Seller, 'id'>) => {
    // 1. Generar id único si no existe
    const sellerId = uuidv4();
    const sellerWithId = { ...seller, id: sellerId };
    await addSeller(sellerWithId);
    // 2. Generar PIN aleatorio
    const pin = Math.floor(1000 + Math.random() * 9000).toString();
    // 3. Crear usuario vendedor
    await addUsuario({
      nombre: seller.name,
      tipo: 'vendedor',
      pin,
      telefono: seller.account || '',
      activo: true,
      fechaCreacion: new Date().toISOString(),
      creadoPor: 'admin',
    });
    // 4. Retornar el PIN generado para mostrarlo/copiarlo
    return pin;
  };

  // En la función addSeller (si existe), asegurar que también genere id si no lo tiene.

  // Estados de carga combinados
  const loading = sellersLoading || lotsLoading || comisionesLoading || movimientosLoading || usuariosLoading || devolucionesLoading;
  const error = sellersError || lotsError || comisionesError || movimientosError || usuariosError || devolucionesError;

  const value = {
    // Datos de Firestore
    lots,
    sellers,
    comisiones,
    expenses: movimientos, // Mantener compatibilidad con el nombre anterior
    movimientos,
    usuarios,
    devoluciones,

    // Estados de carga y error
    loading,
    error,

    // Funciones de Firestore
    addLot,
    updateLot,
    deleteLot,
    addPayment,
    returnLot,
    addSeller,
    updateSeller,
    deleteSeller,
    addComision,
    updateComision,
    deleteComision,
    markComisionAsPaid,
    addMovimiento,
    updateMovimiento,
    deleteMovimiento,
    addUsuario,
    updateUsuario,
    deleteUsuario,
    toggleUsuarioActivo,
    generarNuevoPin,
    buscarPorCredenciales,
    registrarIntentoFallido,
    resetearIntentosFallidos,
    // --- NUEVO: Exponer métodos de refresco global ---
    fetchLots,
    fetchComisiones,
    fetchMovimientos,
    fetchUsuarios,
    fetchDevoluciones,
    addDevolucion,
    aprobarDevolucion,
    rechazarDevolucion,

    // Datos locales (configuración, etc.)
    lotGroups,
    setLotGroups,
    lotHistory,
    setLotHistory,
    markedPayments,
    setMarkedPayments,
    config,
    setConfig,
    configLoading,

    // Funciones de compatibilidad (deprecated)
    setLotsAndPersist,
    setExpensesAndPersist,
    saveAll,
    addSellerWithUser,

    // Función de migración
    migrateMovimientosAutomatic,

    // Setters directos (solo para datos locales)
    setLots: () => console.warn('setLots is deprecated. Use addLot, updateLot, deleteLot instead.'),
    setSellers: () => console.warn('setSellers is deprecated. Use addSeller, updateSeller, deleteSeller instead.'),
    setExpenses: () => console.warn('setExpenses is deprecated. Use addMovimiento, updateMovimiento, deleteMovimiento instead.'),
  };

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  );
}

// 3. Hook que consume el contexto
export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData debe usarse dentro de <AppDataProvider>');
  return ctx;
} 